import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { withRouter } from 'react-router-dom';
import * as queryString from 'query-string';
import { parsePhoneNumber } from 'react-phone-number-input';
import moment from 'moment';

import { moderateUserName } from '../../../api/moderationApi';
import { getApiAxiosInstance } from '../../../utils/network';
import { useStatsig } from '../../../components/hooks/useStatsig';

import requireAuth from '../../../components/hocs/requireAuth';
import withActivateUtr from '../../../components/hocs/withActivateUtr';
import AdditionalInfoForm from '../../../components/AdditionalInfoForm';
import presetClaimedUser from '../../../components/hocs/presetClaimedUser';
import { getAdditionalInfoDefaultValues, getExtraDisplayFieldsForAdditionInfo, getThirdPartyConnection, isUtrActivationInProgress } from '../../../utils/activateUtr';
import { postAdditionalInfoV2 } from '../../../actions/onboarding-actions';
import { showErrorPopup } from '../../../actions/notification-actions';
import { navigateToReturnUrl } from '../../../utils/navigateToReturnUrl';

import styles from '../nuoV2.scss';
import config from '../../../../config';

const googleMapsClient = require('@google/maps').createClient({
    key: config.googleApiKey,
});

const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const daysPerMonth = [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31];

const PersonalInformationPage = (props) => {
    const { auth, activateUtr, inPopUp, profile, initiatePlayerWithSportTypeId } = props;
    const user = props.auth?.user;
    const startMonth = user?.month ? user.month : null;

    const parsed = queryString.parse(props.location.search);

    const [state, setState] = useState({
        formCompleted: false,
        submitting: false,
        locationString: '',
        days: startMonth ? daysPerMonth[startMonth - 1] : daysPerMonth[0],
        months,
        showPopup: false,
        popUpType: null,
        belowAge: false,
        belowAgeAccpted: false,
        isLocationChosenFromDropdown: true,
        isJuniorPlayer: false, // true if age between 14 and 17
    });

    const formApi = useRef();
    const dispatch = useDispatch();

    const { checkGate, featureGates } = useStatsig();
    const isUserNameModerationEnabled = checkGate(featureGates.USERNAME_MODERATION);

    useEffect(() => {
        const savedLoc = user?.location?.googleFormattedName || profile?.data?.location?.display;

        const parseGeoLocationResponseAndSetLocation = (geoLocationResponse, defaultValue = '') => {
            let locationResults = geoLocationResponse.json.results.filter(x => x.types[0] === 'postal_code');
            if (!locationResults.length) {
                locationResults = geoLocationResponse.json.results.filter(x => x.types[0] === 'administrative_area_level_2');
            }
            if (!locationResults.length) {
                locationResults = geoLocationResponse.json.results.filter(x => x.types[0] === 'administrative_area_level_1');
            }
            const formattedLocation = locationResults.length > 0 ? locationResults[0].formatted_address : defaultValue;
            setState((prevState) => ({
                ...prevState,
                locationString: formattedLocation,
                formCompleted: false,
            }));
            if (formApi.current) {
                formApi.current.setValue('location', formattedLocation);
            }
        };

        if (isUtrActivationInProgress(activateUtr)) {
            const thirdPartyConnection = getThirdPartyConnection(activateUtr.activationType);
            const address = [
                activateUtr.user.state || '',
                activateUtr.user.postcode || '',
                thirdPartyConnection.country,
            ].join(' ');

            googleMapsClient.geocode({
                address,
            }, (err, response) => {
                parseGeoLocationResponseAndSetLocation(response);
            });
        } else if (savedLoc) {
            setState((prevState) => ({
                ...prevState,
                locationString: savedLoc,
                formCompleted: false,
            }));
        } else if ('geolocation' in navigator) {
            navigator.geolocation.getCurrentPosition(position => {
                const { latitude, longitude } = position.coords;
                googleMapsClient.reverseGeocode({
                    latlng: [latitude, longitude],
                }, (err, response) => {
                    parseGeoLocationResponseAndSetLocation(response);
                });
            });
        }
    }, []);

    useEffect(() => {
        if (auth.user.onboardingStep > 1 && !auth.saving && state.formCompleted) {
            const hasClaimedProfile = auth.user.claimedPlayer != null;
            if (hasClaimedProfile) {
                if (inPopUp) {
                    props.closePopup();
                } else {
                    props.history.push({ pathname: '/home' });
                }
                return;
            }

            if (inPopUp) {
                props.moveToNextScreen();
            } else {
                const returnPage = parsed.return;
                const inviteToken = parsed?.token;
                const inviteId = parsed?.id;
                const coachRedirectUrl = returnPage || '/coach';
                let redirectUrl;
                if (inviteToken && inviteId) {
                    redirectUrl = auth.user.onboardingRequired ? `/getrated${returnPage ? `?return=${encodeURIComponent(returnPage)}&id=${inviteId}&token=${inviteToken}` : ''}` : coachRedirectUrl;
                } else {
                    redirectUrl = auth.user.onboardingRequired ? `/getrated${returnPage ? `?return=${encodeURIComponent(returnPage)}` : ''}` : coachRedirectUrl;
                }
                navigateToReturnUrl(props.history, redirectUrl);
            }
        }
    }, [auth.user.onboardingStep, auth.saving, state.formCompleted, auth.user.onboardingRequired]);

    useEffect(() => {
        if (props.nuo.error) {
            setState((prevState) => ({
                ...prevState,
                ladditionalInfoError: props.nuo.error,
            }));
        }
    }, [props.nuo]);

    const exposeFormApi = (formApi) => {
        formApi.current = formApi;
    };

    const handleLowAgeCheck = (val) => {
        setState((prevState) => ({
            ...prevState,
            belowAgeAccpted: val,
        }));
    };

    const handleAutocomplete = (locationString) => {
        setState((prevState) => ({
            ...prevState,
            locationString,
            isLocationChosenFromDropdown: true,
        }));
    };

    const handleLocationChange = () => {
        setState((prevState) => ({
            ...prevState,
            isLocationChosenFromDropdown: false,
        }));
    };

    const handleDOBChange = (values) => {
        const month = months.indexOf(values.month);
        const { year, day } = values;

        // month is 0 indexed so -1 if not selected
        if (day && month >= 0 && year) {
            const date = moment().year(year).month(month).date(day);

            const diffInYears = moment().diff(date, 'years');
            if (diffInYears <= 13) {
                setState((prevState) => ({
                    ...prevState,
                    belowAge: true,
                    isJuniorPlayer: false,
                }));
            } else if (diffInYears >= 14 && diffInYears <= 17) {
                setState((prevState) => ({
                    ...prevState,
                    isJuniorPlayer: true,
                    belowAge: false,
                }));
            } else {
                setState((prevState) => ({
                    ...prevState,
                    belowAge: false,
                    belowAgeAccpted: false,
                    additionalInfoError: '',
                    isJuniorPlayer: false,
                }));
            }
        }
    };

    const handleMonthChange = (val) => {
        const monthVal = months.indexOf(val);
        setState((prevState) => ({
            ...prevState,
            days: val ? daysPerMonth[monthVal] : daysPerMonth[0],
        }));
    };

    const togglePopup = (e, popUpType) => {
        e.preventDefault();
        setState((prevState) => ({
            ...prevState,
            showPopup: !prevState.showPopup,
            popUpType,
        }));
    };

    const isValidBirthdate = (date, diffInYears) => {
        if (date >= new Date()) {
            setState((prevState) => ({
                ...prevState,
                additionalInfoError: 'Birthdate should not be greater than today',
            }));
            return false;
        }
        if (diffInYears < 13 && !state.belowAgeAccpted) {
            if (!state.belowAge) {
                setState((prevState) => ({
                    ...prevState,
                    belowAge: true,
                }));
            } else if (state.belowAge && !state.belowAgeAccpted) {
                setState((prevState) => ({
                    ...prevState,
                    additionalInfoError: 'Please accept the conditions to proceed',
                }));
            }
            return false;
        }

        return true;
    };

    const validateUserName = async (values) => {
        if (isUserNameModerationEnabled) {
            try {
                const moderationResult = await moderateUserName(
                    getApiAxiosInstance(),
                    { firstName: values.firstName, lastName: values.lastName },
                );
                const { data } = moderationResult;
                if (!data.ok) {
                    dispatch(showErrorPopup({
                        message: 'Error posting onboarding data',
                        error: data.message,
                        showSupportLink: true,
                    }));
                    return false;
                }
            } catch (err) {
                // Ignore errors here, we'll check again on the server
            }
        }

        return true;
    };

    const handleSubmit = async (values) => {
        setState((prevState) => ({
            ...prevState,
            submitting: true,
        }));

        // validate birthdate
        values.month = Math.max(months.indexOf(values.month), 0) + 1;
        const date = moment().year(values.year).month(values.month - 1).date(values.day);
        const diffInYears = moment().diff(date, 'years');
        const isBirthdateValid = isValidBirthdate(date, diffInYears);

        if (!isBirthdateValid || !await validateUserName(values)) {
            setState((prevState) => ({
                ...prevState,
                submitting: false,
            }));
            return;
        }

        values.location = state.locationString;
        values.phoneCountry = parsePhoneNumber(values.phone).country;
        if (!state.isJuniorPlayer && values.secondaryEmail) {
            // eslint-disable-next-line no-param-reassign
            values.secondaryEmail = undefined;
        }
        if (!state.belowAge && values.belowAgeCheckbox) {
            // eslint-disable-next-line no-param-reassign
            values.belowAgeCheckbox = undefined;
        }
        if (parsed.id && parsed.token) {
            values.inviteToken = parsed.token;
            values.inviteId = parsed.id;
        }

        try {
            await dispatch(postAdditionalInfoV2(values));
        } catch (error) {
            setState((prevState) => ({
                ...prevState,
                submitting: false,
            }));
        }
    };

    const showCoachBtn = auth && auth.user && auth.user.requiredOnboardingFlowType !== 'Player' && auth.user.requiredOnboardingFlowType !== 'NoProfileMember';

    const inputProps = {
        placeholder: 'Location (zip or city)',
        name: 'locationString',
    };
    let defaultValues = {
        initiatePlayerWithSportTypeId,
    };
    let defaultPhoneCountryCode = 'US';
    let hideLabels = true;
    let additionalDisplayFields = [];
    if (isUtrActivationInProgress(activateUtr)) {
        defaultValues = getAdditionalInfoDefaultValues(activateUtr);
        hideLabels = false;
        additionalDisplayFields = getExtraDisplayFieldsForAdditionInfo(activateUtr);
        defaultPhoneCountryCode = getThirdPartyConnection(activateUtr.activationType).countryCode;
    }

    return (
        <div className={styles.pageContent}>
            <div className={styles.title} data-testid="nuo-v2.personal-information.title">
                Player Profile Information
            </div>
            <AdditionalInfoForm
                handleMonthChange={handleMonthChange}
                onFormChange={handleDOBChange}
                handleAutocomplete={handleAutocomplete}
                handleLocationChange={handleLocationChange}
                onSubmit={handleSubmit}
                submitting={state.submitting}
                inputProps={inputProps}
                exposeFormApi={exposeFormApi}
                redirectUrl={'/playerorcoach'}
                days={state.days}
                months={state.months}
                togglePopup={togglePopup}
                showPopup={state.showPopup}
                popUpType={state.popUpType}
                belowAge={state.belowAge}
                belowAgeAccpted={state.belowAgeAccpted}
                error={state.additionalInfoError}
                handleLowAgeCheck={handleLowAgeCheck}
                showCoachBtn={showCoachBtn}
                requiredOnboardingFlowType={auth.user.requiredOnboardingFlowType}
                isJuniorPlayer={state.isJuniorPlayer}
                isLocationChosenFromDropdown={state.isLocationChosenFromDropdown}
                defaultValues={defaultValues}
                hideLabels={hideLabels}
                additionalDisplayFields={additionalDisplayFields}
                defaultPhoneCountryCode={defaultPhoneCountryCode}
                themeV3
            />
        </div>
    );
};

export default withRouter(requireAuth(withActivateUtr(presetClaimedUser(PersonalInformationPage))));
